import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import '../styles/auth.css';
import {
  getPasswordRules,
  isPasswordValid,
  requestPasswordReset,
  resetPassword,
  verifyRecoveryCode,
} from '../services/passwordRecoveryService';

const MailIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const LockIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 118 0v3" />
  </svg>
);

const EyeIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const RuleIcon = ({ met }) => (
  <span className={`recovery-rule-icon${met ? ' recovery-rule-icon--met' : ''}`} aria-hidden="true">
    {met ? '✓' : '✕'}
  </span>
);

const passwordRuleItems = [
  { id: 'minLength', label: 'Longitud Mínima: Al menos 12 caracteres.' },
  { id: 'mixedCase', label: 'Letras mayúsculas y minúsculas (Ejemplo: AaBb).' },
  { id: 'numbers', label: 'Números (Ejemplo: 123).' },
  { id: 'symbols', label: 'Símbolos especiales (Ejemplo: *@#!?)' },
];

const EmailSentNotice = ({ onClose }) => (
  <div className="recovery-notice-overlay" role="dialog" aria-modal="true" aria-labelledby="recovery-notice-title">
    <div className="recovery-notice-modal">
      <button type="button" className="recovery-notice-close" onClick={onClose} aria-label="Cerrar aviso">
        ✕
      </button>
      <div className="recovery-status-icon recovery-status-icon--success" aria-hidden="true">
        ✓
      </div>
      <h2 id="recovery-notice-title" className="register-success-title">
        ¡Código Enviado con Éxito!
      </h2>
      <p className="register-success-text">
        Hemos enviado un correo electrónico con tu código de verificación de 6 dígitos. Revisa tu bandeja de
        entrada (y la carpeta de spam si no lo encuentras). El código expira en 15 minutos.
      </p>
      <button type="button" className="auth-submit register-success-btn" onClick={onClose}>
        Continuar
      </button>
    </div>
  </div>
);

const RecoveryStatusCard = ({ variant, title, text, actionLabel, onAction, actionTo }) => {
  const isSuccess = variant === 'success';
  const isFailure = variant === 'failure';

  return (
    <div className="auth-root">
      <section className="auth-section">
        <div className="recovery-status-card">
          <div
            className={`recovery-status-icon${
              isSuccess ? ' recovery-status-icon--success' : ''
            }${isFailure ? ' recovery-status-icon--failure' : ''}`}
            aria-hidden="true"
          >
            {isSuccess ? '✓' : '✕'}
          </div>
          <h1 className="register-success-title">{title}</h1>
          {text ? <p className="register-success-text">{text}</p> : null}
          {actionTo ? (
            <Link to={actionTo} className="auth-submit register-success-btn">
              {actionLabel}
            </Link>
          ) : (
            <button type="button" className="auth-submit register-success-btn" onClick={onAction}>
              {actionLabel}
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

const ResetPasswordForm = ({
  email,
  verificationCode,
  codeVerified,
  codeErrorMessage,
  password,
  confirmPassword,
  showPassword,
  showConfirmPassword,
  passwordRules,
  errorMessage,
  isVerifyingCode,
  isSubmitting,
  onVerificationCodeChange,
  onVerifyCode,
  onPasswordChange,
  onConfirmPasswordChange,
  onTogglePassword,
  onToggleConfirmPassword,
  onSubmit,
}) => (
  <div className="auth-root">
    <section className="auth-section">
      <div className="recovery-shell">
        <aside className="recovery-visual">
          <h1 className="recovery-visual-title">Cambio de Contraseña</h1>
          <img
            src={`${process.env.PUBLIC_URL}/images/recovery-reset-photo.png`}
            alt="Niños sonriendo"
            className="recovery-photo"
          />
        </aside>

        <div className="recovery-panel">
          <form className="recovery-form" onSubmit={onSubmit}>
            <p className="recovery-panel-intro">
              {codeVerified
                ? 'Código verificado. Ahora ingresa tu nueva contraseña.'
                : 'Ingresa el código de 6 dígitos que recibiste en tu correo.'}
            </p>

            {email ? (
              <p className="recovery-panel-email" aria-live="polite">
                Correo: <strong>{email}</strong>
              </p>
            ) : null}

            {!codeVerified ? (
              <>
                <label className="auth-field">
                  <span className="auth-field-control">
                    <MailIcon />
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      placeholder="Código de verificación"
                      value={verificationCode}
                      onChange={onVerificationCodeChange}
                      autoComplete="one-time-code"
                      required
                    />
                  </span>
                </label>

                {codeErrorMessage ? <p className="register-error">{codeErrorMessage}</p> : null}

                <div className="register-wizard-actions">
                  <button
                    type="button"
                    className="register-btn-primary"
                    onClick={onVerifyCode}
                    disabled={isVerifyingCode}
                  >
                    {isVerifyingCode ? 'Verificando...' : 'Continuar >'}
                  </button>
                </div>
              </>
            ) : null}

            <label className="auth-field">
              <span className={`auth-field-control${codeVerified ? '' : ' auth-field-control--disabled'}`}>
                <LockIcon />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Contraseña"
                  value={password}
                  onChange={onPasswordChange}
                  disabled={!codeVerified}
                  required={codeVerified}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={onTogglePassword}
                  disabled={!codeVerified}
                  aria-label="Mostrar u ocultar contraseña"
                >
                  <EyeIcon />
                </button>
              </span>
            </label>

            <label className="auth-field">
              <span className={`auth-field-control${codeVerified ? '' : ' auth-field-control--disabled'}`}>
                <LockIcon />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirmar contraseña"
                  value={confirmPassword}
                  onChange={onConfirmPasswordChange}
                  disabled={!codeVerified}
                  required={codeVerified}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={onToggleConfirmPassword}
                  disabled={!codeVerified}
                  aria-label="Mostrar u ocultar confirmación"
                >
                  <EyeIcon />
                </button>
              </span>
            </label>

            <ul className={`recovery-rules${codeVerified ? '' : ' recovery-rules--disabled'}`}>
              {passwordRuleItems.map((rule) => (
                <li
                  key={rule.id}
                  className={`recovery-rule${passwordRules[rule.id] ? ' recovery-rule--met' : ''}`}
                >
                  <RuleIcon met={passwordRules[rule.id]} />
                  <span>{rule.label}</span>
                </li>
              ))}
            </ul>

            {errorMessage ? <p className="register-error">{errorMessage}</p> : null}

            {codeVerified ? (
              <div className="register-wizard-actions">
                <button type="submit" className="register-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Actualizando...' : 'Actualizar contraseña >'}
                </button>
              </div>
            ) : null}
          </form>
        </div>
      </div>
    </section>
  </div>
);

const ForgotPasswordPage = () => {
  const history = useHistory();
  const location = useLocation();
  const isResetRoute = location.pathname.includes('/restablecer');
  const queryEmail = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('email') || '';
  }, [location.search]);

  const [step, setStep] = useState(isResetRoute ? 3 : 1);
  const [email, setEmail] = useState(queryEmail);
  const [showEmailSentNotice, setShowEmailSentNotice] = useState(
    Boolean(location.state && location.state.showEmailNotice)
  );
  const [verificationCode, setVerificationCode] = useState('');
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeErrorMessage, setCodeErrorMessage] = useState('');
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordRules = getPasswordRules(password);

  useEffect(() => {
    if (queryEmail) {
      setEmail(queryEmail);
    }
  }, [queryEmail]);

  const handleSendCode = (event) => {
    event.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    const normalizedEmail = email.trim().toLowerCase();

    requestPasswordReset(email)
      .then(() => {
        setStep(3);
        setVerificationCode('');
        setCodeVerified(false);
        setCodeErrorMessage('');
        setPassword('');
        setConfirmPassword('');
        setShowEmailSentNotice(true);
        history.push({
          pathname: '/recuperar-contraseña/restablecer',
          search: `?email=${encodeURIComponent(normalizedEmail)}`,
          state: { showEmailNotice: true },
        });
      })
      .catch((error) => {
        setErrorMessage(error.message || 'No se pudo enviar el código.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleVerifyCode = () => {
    setCodeErrorMessage('');
    setErrorMessage('');

    if (!/^\d{6}$/.test(verificationCode.trim())) {
      setCodeErrorMessage('Ingresa un código de 6 dígitos.');
      return;
    }

    setIsVerifyingCode(true);

    verifyRecoveryCode({ email, codigo: verificationCode })
      .then(() => {
        setCodeVerified(true);
        setCodeErrorMessage('');
      })
      .catch((error) => {
        setCodeVerified(false);
        setCodeErrorMessage(error.message || 'No se pudo verificar el código.');
      })
      .finally(() => {
        setIsVerifyingCode(false);
      });
  };

  const handleUpdatePassword = (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!codeVerified) {
      setErrorMessage('Debes verificar el código antes de cambiar la contraseña.');
      return;
    }

    if (!isPasswordValid(password)) {
      setErrorMessage('La contraseña no cumple con los requisitos.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Las contraseñas deben coincidir.');
      return;
    }

    setIsSubmitting(true);
    resetPassword({ email, password, confirmPassword })
      .then(() => {
        setStep(4);
      })
      .catch((error) => {
        setStep(5);
        setErrorMessage(error.message || 'No se pudo actualizar la contraseña.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleCloseEmailNotice = () => {
    setShowEmailSentNotice(false);
    history.replace({
      pathname: '/recuperar-contraseña/restablecer',
      search: location.search,
    });
  };

  if (step === 4) {
    return (
      <RecoveryStatusCard
        variant="success"
        title="Acción Realizada Exitosamente"
        text="Tu contraseña se ha cambiado con éxito"
        actionLabel="Iniciar sesión"
        actionTo="/iniciar-sesion"
      />
    );
  }

  if (step === 5) {
    return (
      <RecoveryStatusCard
        variant="failure"
        title="Acción Fallida"
        text={errorMessage || 'Por favor intente esta acción nuevamente'}
        actionLabel="Intentar nuevamente"
        onAction={() => {
          setErrorMessage('');
          setCodeErrorMessage('');
          setVerificationCode('');
          setCodeVerified(false);
          setStep(isResetRoute ? 3 : 1);
        }}
      />
    );
  }

  if (step === 3) {
    return (
      <>
        {showEmailSentNotice ? <EmailSentNotice onClose={handleCloseEmailNotice} /> : null}
        <ResetPasswordForm
          email={email}
          verificationCode={verificationCode}
          codeVerified={codeVerified}
          codeErrorMessage={codeErrorMessage}
          password={password}
          confirmPassword={confirmPassword}
          showPassword={showPassword}
          showConfirmPassword={showConfirmPassword}
          passwordRules={passwordRules}
          errorMessage={errorMessage}
          isVerifyingCode={isVerifyingCode}
          isSubmitting={isSubmitting}
          onVerificationCodeChange={(event) => {
            const value = event.target.value.replace(/\D/g, '').slice(0, 6);
            setVerificationCode(value);
          }}
          onVerifyCode={handleVerifyCode}
          onPasswordChange={(event) => setPassword(event.target.value)}
          onConfirmPasswordChange={(event) => setConfirmPassword(event.target.value)}
          onTogglePassword={() => setShowPassword((value) => !value)}
          onToggleConfirmPassword={() => setShowConfirmPassword((value) => !value)}
          onSubmit={handleUpdatePassword}
        />
      </>
    );
  }

  return (
    <div className="auth-root">
      <section className="auth-section">
        <div className="recovery-shell">
          <aside className="recovery-visual">
            <h1 className="recovery-visual-title">Recuperación de Contraseña</h1>
            <img
              src={`${process.env.PUBLIC_URL}/images/recovery-request-photo.png`}
              alt="Niño con mochila"
              className="recovery-photo"
            />
          </aside>

          <div className="recovery-panel">
            <form className="recovery-form" onSubmit={handleSendCode}>
              <p className="recovery-panel-intro">
                Ingresa tu correo electrónico. Te enviaremos un código de verificación de 6 dígitos para
                restablecer tu contraseña.
              </p>

              <label className="auth-field">
                <span className="auth-field-control">
                  <MailIcon />
                  <input
                    type="email"
                    placeholder="Correo electrónico"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </span>
              </label>

              {errorMessage ? <p className="register-error">{errorMessage}</p> : null}

              <div className="register-wizard-actions">
                <button
                  type="button"
                  className="register-btn-secondary"
                  onClick={() => history.push('/iniciar-sesion')}
                >
                  Volver
                </button>
                <button type="submit" className="register-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Enviando...' : 'Enviar código >'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForgotPasswordPage;
